import React, { useState, useRef, useEffect, useCallback } from "react";
import { MapContainer, TileLayer } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";
import { useMap, useMapEvents } from "react-leaflet";
import { Dialog } from "primereact/dialog";
import { Toast } from "primereact/toast";
import "leaflet-geometryutil";
import { Checkbox } from "primereact/checkbox";

// Solución para que se vean los marcadores
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

const SetViewOnChange = ({ position, zoom }) => {
  const map = useMap();

  useEffect(() => {
    map.setView(position, zoom);
  }, [position, zoom, map]);

  return null;
};

const MapZoomClassController = ({ currentZoom, minZoom }) => {
  const map = useMap();

  useEffect(() => {
    const el = map.getContainer();
    if (!el) return;

    const ok = Number(currentZoom) >= Number(minZoom);

    el.classList.toggle("map-clickable", ok);
    el.classList.toggle("map-need-zoom", !ok);
  }, [map, currentZoom, minZoom]);

  return null;
};


const TrackZoom = ({ onZoomChange }) => {
  const map = useMap();

  // Inicializa al montar (muy importante)
  useEffect(() => {
    onZoomChange(map.getZoom());
  }, [map, onZoomChange]);

  useMapEvents({
    zoom: () => {
      onZoomChange(map.getZoom()); // se dispara durante el zoom
    },
    zoomend: () => {
      onZoomChange(map.getZoom()); // y también al terminar
    },
  });

  return null;
};



const EnableZoomWithCtrl = () => {
  const map = useMap();

  useEffect(() => {
    const container = map.getContainer();

    const handleWheel = (e) => {
      if (e.ctrlKey) {
        e.preventDefault(); // Previene zoom del navegador
        map.scrollWheelZoom.enable();

        setTimeout(() => {
          map.scrollWheelZoom.disable();
        }, 200);
      }
    };

    container.addEventListener("wheel", handleWheel, { passive: false });

    return () => {
      container.removeEventListener("wheel", handleWheel);
    };
  }, [map]);

  return null;
};

const ClickHandler = ({ onStreetClick }) => {
  const map = useMap();

  useMapEvents({
    click: (e) => {
      if (onStreetClick) onStreetClick(map, e.latlng);
    },
  });

  return null;
};

const getFeature = (geoinfo) => {
  // Normaliza entradas muy variables (string / objeto wrapper / feature)
  let feature = geoinfo;
  if (typeof feature === "string") {
    feature = JSON.parse(feature);
  }

  if (feature && typeof feature === "object") {
    if ("geometry" in feature) return feature; // ya es GeoJSON Feature
    if ("feature" in feature) return JSON.parse(feature.feature); // wrapper {feature:"..."}
    if ("properties" in feature) return feature.properties;
  }

  return feature;
};

// --- Tolerancia dinámica: de píxeles a metros ---
function metersPerPixel(lat, zoom) {
  return (156543.03392 * Math.cos((lat * Math.PI) / 180)) / Math.pow(2, zoom);
}

function toleranceMetersForMap(map, pixelTolerance = 3, minMeters = 0) {
  const center = map.getCenter();
  const mpp = metersPerPixel(center.lat, map.getZoom());
  return Math.max(mpp * pixelTolerance, minMeters);
}

// --- Distancia click->feature (LineString / MultiLineString) ---
function isClickNearFeature(map, clickedLatLng, feature, toleranceMeters) {
  if (!feature?.geometry?.type || !feature?.geometry?.coordinates) return false;

  const { type, coordinates } = feature.geometry;

  const lines =
    type === "MultiLineString"
      ? coordinates // array de líneas
      : type === "LineString"
      ? [coordinates] // una sola línea
      : []; // otros tipos: no evaluamos aquí

  for (const line of lines) {
    for (let j = 0; j < line.length - 1; j++) {
      // GeoJSON es [lng, lat]
      const p1 = L.latLng(line[j][1], line[j][0]);
      const p2 = L.latLng(line[j + 1][1], line[j + 1][0]);

      const closestPoint = L.GeometryUtil.closestOnSegment(map, clickedLatLng, p1, p2);
      const distance = clickedLatLng.distanceTo(closestPoint);

      if (distance <= toleranceMeters) return true;
    }
  }

  return false;
}
/* version 2.0 */
const MapaMudo = ({ callejero }) => {
  const [calles, setCalles] = useState([]);
  const [tiposCalle, setTiposCalle] = useState([]);
  const [mapConfig, setMapConfig] = useState({});
  const [position, setPosition] = useState([51.505, -0.09]);
  const [zoom, setZoom] = useState(13);
  const [calleActual, setCalleActual] = useState(null);

  const [aciertos, setAciertos] = useState(0);
  const [fallos, setFallos] = useState(0);
  const [porcentajeAciertos, setPorcentajeAciertos] = useState(0);

  const [mostrarNota, setMostrarNota] = useState(false);
  const [clickHabilitado, setClickHabilitado] = useState(true);
  const [ultimoResultado, setUltimoResultado] = useState(null);
  const [mostrarInstrucciones, setMostrarInstrucciones] = useState(true);

  const [urlLayer, setUrlLayer] = useState("");
  const [attributionLayer, setAttributionLayer] = useState("");
  const [ayuda, setAyuda] = useState(false);
  const [zoomWarning, setZoomWarning] = useState(false);
  const zoomWarningTimeoutRef = useRef(null);


  const toast = useRef(null);

  // Capas pintadas (para limpiar)
  const previousLayersRef = useRef([]);

  // Evitar “mensajes cruzados” de Nominatim: token de intento + abort
  const attemptIdRef = useRef(0);
  const nominatimAbortRef = useRef(null);

  // Refs para evitar estados “stale” dentro del handler
  const calleActualRef = useRef(calleActual);
  const callesRef = useRef(calles);
  const positionRef = useRef(position);
  const zoomRef = useRef(zoom);

  const [currentZoom, setCurrentZoom] = useState(zoom);


  // zoom minimo para confirmar
  const MIN_ZOOM_TO_CONFIRM = 16;

  useEffect(() => {
    calleActualRef.current = calleActual;
  }, [calleActual]);

  useEffect(() => {
    callesRef.current = calles;
  }, [calles]);

  useEffect(() => {
    positionRef.current = position;
  }, [position]);

  useEffect(() => {
    zoomRef.current = zoom;
  }, [zoom]);

  useEffect(() => {
    return () => {
      if (zoomWarningTimeoutRef.current) clearTimeout(zoomWarningTimeoutRef.current);
    };
  }, []);


  // Cleanup general al desmontar
  useEffect(() => {
    return () => {
      if (nominatimAbortRef.current) nominatimAbortRef.current.abort();
      // OJO: sin map aquí, pero al desmontar Leaflet limpia
      previousLayersRef.current = [];
    };
  }, []);

  const onAyudaChange = (e) => setAyuda(e.checked);

  const getTipoCalle = useCallback(
    (tipoId) => {
      const tipo = tiposCalle.find((t) => String(t.ID) === String(tipoId));
      return tipo ? tipo.nombre : "Desconocido";
    },
    [tiposCalle]
  );

  const getCalle = useCallback(() => {
    if (!Array.isArray(calles) || calleActual == null || !calles[calleActual]) return "Cargando...";
    const tipo = getTipoCalle(calles[calleActual].tipoCalle);
    return `${calles[calleActual].nombre} (${tipo})`;
  }, [calles, calleActual, getTipoCalle]);

  const recargarPagina = () => window.location.reload();

  // ---- Handler principal ----
  const handleStreetClick = useCallback((map, coords) => {
    const currentIndex = calleActualRef.current;
    const currentCalles = callesRef.current;

    if (currentIndex == null || !currentCalles?.[currentIndex]) return;

    // Si el click está deshabilitado, este click sirve para avanzar
    if (!clickHabilitado) {
      // limpiar capas pintadas
      previousLayersRef.current.forEach((layer) => map.removeLayer(layer));
      previousLayersRef.current = [];

      setUltimoResultado(null);

      if (currentIndex < currentCalles.length - 1) {
        setCalleActual((c) => (c == null ? 0 : c + 1));
        setClickHabilitado(true);

        // volver a vista inicial
        map.setView(positionRef.current, zoomRef.current);
      } else {
        // finalizar
        const total = (aciertos || 0) + (fallos || 0);
        const pct = total > 0 ? ((aciertos / total) * 100).toFixed(2) : "0.00";
        setPorcentajeAciertos(pct);
        setMostrarNota(true);
      }

      return;
    }

    // SOLO para el click de evaluación (no para el click de avanzar)
    
    if (map.getZoom() < MIN_ZOOM_TO_CONFIRM) {
      setZoomWarning(true);

      if (zoomWarningTimeoutRef.current) clearTimeout(zoomWarningTimeoutRef.current);
      zoomWarningTimeoutRef.current = setTimeout(() => {
        setZoomWarning(false);
      }, 2500); // 2.5s

      return; // NO confirmar selección
    }

    // ---- Click de evaluación ----
    setClickHabilitado(false);

    // Limpia mensaje anterior AL EMPEZAR un nuevo intento
    setUltimoResultado(null);

    // nuevo intento + aborta fetch anterior
    attemptIdRef.current += 1;
    const myAttemptId = attemptIdRef.current;

    if (nominatimAbortRef.current) nominatimAbortRef.current.abort();
    const controller = new AbortController();
    nominatimAbortRef.current = controller;

    // limpiar capas anteriores por si quedaba algo (extra defensivo)
    previousLayersRef.current.forEach((layer) => map.removeLayer(layer));
    previousLayersRef.current = [];

    const clickedLatLng = L.latLng(coords.lat, coords.lng);

    // Normalizar features desde geoinfo (array u objeto)
    let features = [];
    const geoinfo = currentCalles[currentIndex].geoinfo;

    if (Array.isArray(geoinfo)) features = geoinfo;
    else if (geoinfo && typeof geoinfo === "object") features = [geoinfo];

    // Si hay 1 elemento wrapper, puede contener FeatureCollection
    if (features.length === 1) {
      try {
        const raw = features[0];
        const parsed = raw?.feature ? JSON.parse(raw.feature) : getFeature(raw);
        if (parsed?.type?.toUpperCase() === "FEATURECOLLECTION") {
          features = parsed.features || [];
        }
      } catch {
        // si falla, seguimos con lo que haya
      }
    }

    // Tolerancia: en píxeles -> metros, con mínimo para carreteras
    // (ajusta estos valores si quieres)
    const pixelTol = 3; // 3px suele ser buen equilibrio
    const minMeters = 15; // mínimo recomendado (carreteras / zoom bajo / táctil)
    const tolMeters = toleranceMetersForMap(map, pixelTol, minMeters);

    // 1) Validación: ¿cerca de cualquiera de los features?
    let cerca = false;
    for (let f = 0; f < features.length && !cerca; f++) {
      const feat = getFeature(features[f]);

      // FeatureCollection anidado (por si acaso)
      if (feat?.type?.toUpperCase() === "FEATURECOLLECTION" && Array.isArray(feat.features)) {
        for (const sub of feat.features) {
          if (isClickNearFeature(map, clickedLatLng, sub, tolMeters)) {
            cerca = true;
            break;
          }
        }
      } else {
        if (isClickNearFeature(map, clickedLatLng, feat, tolMeters)) cerca = true;
      }
    }

    const colorFeature = cerca ? "green" : "red";

    // 2) Pintado + fitBounds (unión de bounds de todos)
    const group = L.featureGroup();
    for (let f = 0; f < features.length; f++) {
      const feat = getFeature(features[f]);

      // Si es FeatureCollection, pinta sus features
      if (feat?.type?.toUpperCase() === "FEATURECOLLECTION" && Array.isArray(feat.features)) {
        for (const sub of feat.features) {
          const layer = L.geoJSON(sub, { style: { color: colorFeature, weight: 4 } });
          layer.addTo(map);
          previousLayersRef.current.push(layer);
          group.addLayer(layer);
        }
      } else {
        const layer = L.geoJSON(feat, { style: { color: colorFeature, weight: 4 } });
        layer.addTo(map);
        previousLayersRef.current.push(layer);
        group.addLayer(layer);
      }
    }

    try {
      const bounds = group.getBounds();
      if (bounds && bounds.isValid()) map.fitBounds(bounds);
    } catch {
      // ignore
    }

    // 3) Resultado: acierto / fallo + (si fallo) Nominatim
    if (cerca) {
      setAciertos((a) => a + 1);
      // ultimoResultado debe permanecer null para mostrar mensaje de acierto
    } else {
      setFallos((f) => f + 1);

      fetch(
        `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${coords.lat}&lon=${coords.lng}&namedetails=1`,
        {
          signal: controller.signal,
          headers: { "Accept-Language": "es" },
        }
      )
        .then((r) => r.json())
        .then((data) => {
          // Evitar que un fetch antiguo pise el estado del intento actual
          if (attemptIdRef.current !== myAttemptId) return;

          const road = data?.address?.road;
          if (road) setUltimoResultado(String(road).toUpperCase());
          else setUltimoResultado("CALLE DESCONOCIDA");
        })
        .catch((err) => {
          if (err?.name === "AbortError") return;
          console.error("Error en geocodificación inversa:", err);
        });
    }
  }, [clickHabilitado, aciertos, fallos]);

  // ---- Carga del callejero ----
  useEffect(() => {
    if (callejero) {
      setCalles(callejero.callejero || []);
      setTiposCalle(callejero.tiposCalle || []);

      if (typeof callejero.mapConfig === "string") {
        try {
          const parsedConfig = JSON.parse(callejero.mapConfig);
          setMapConfig(parsedConfig);

          if (parsedConfig.lat && parsedConfig.lng) setPosition([parsedConfig.lat, parsedConfig.lng]);
          else setPosition([51.505, -0.09]);

          if (parsedConfig.zoom) setZoom(parsedConfig.zoom);
          else setZoom(13);
        } catch (error) {
          console.error("Error parsing mapConfig:", error);
          setPosition([51.505, -0.09]);
          setZoom(13);
        }
      } else {
        setPosition([51.505, -0.09]);
        setZoom(13);
      }

      if (callejero.tipoMapa) {
        if (callejero.tipoMapa === "CALLEJERO") {
          setUrlLayer("https://{s}.basemaps.cartocdn.com/light_nolabels/{z}/{x}/{y}{r}.png");
          setAttributionLayer('&copy; <a href="https://carto.com/">CARTO</a>');
        } else {
          setUrlLayer("https://{s}.basemaps.cartocdn.com/rastertiles/voyager_labels_under/{z}/{x}/{y}{r}.png");
          setAttributionLayer("&copy; OpenStreetMap &copy; CARTO");
        }
      } else {
        setUrlLayer("https://{s}.basemaps.cartocdn.com/light_nolabels/{z}/{x}/{y}{r}.png");
        setAttributionLayer('&copy; <a href="https://carto.com/">CARTO</a>');
      }
    } else {
      setCalles([]);
      setMapConfig({});
      setUrlLayer("https://{s}.basemaps.cartocdn.com/light_nolabels/{z}/{x}/{y}{r}.png");
      setAttributionLayer('&copy; <a href="https://carto.com/">CARTO</a>');
      setPosition([51.505, -0.09]);
      setZoom(13);
      setMostrarNota(false);
      setAciertos(0);
      setFallos(0);
      setCalleActual(null);
      setUltimoResultado(null);
      setClickHabilitado(true);
    }
  }, [callejero]);

  useEffect(() => {
    if (calles && calles.length > 0) setCalleActual(0);
  }, [calles]);

  // ---- Render ----
  if (mostrarInstrucciones) {
    return (
      <>
        <Dialog
          visible={mostrarInstrucciones}
          style={{ width: "50vw" }}
          onHide={() => setMostrarInstrucciones(false)}
          draggable={false}
          resizable={false}
          modal={true}
          showHeader={true}
          closable={true}
          header="Bienvenid@ a la práctica de Callejero de Academia Bomberos Elche"
        >
          <div>
            <p>
              En esta práctica, tendrás que identificar calles en un mapa sin nombres visibles. En la parte superior verás una placa con el nombre de la calle que debes identificar.
            </p>
            <p>Para desplazarte por el mapa, haz clic izquierdo y, sin soltar, arrastra el mapa en la dirección deseada.</p>
            <p>Pulsando y manteniendo la tecla Control, y con la ruleta del ratón, podrás acercar o alejar el mapa en la posición actual.</p>
            <p>
              Para seleccionar la calle que crees correcta, haz clic izquierdo con el ratón. Tras esto, en la placa superior verás si has acertado o fallado. Para continuar con la siguiente calle, vuelve a hacer clic en el mapa.
            </p>
            <button className="btn btn-primary" onClick={() => setMostrarInstrucciones(false)}>
              Comenzar
            </button>
          </div>
        </Dialog>
      </>
    );
  }

  return (
    <>
      <Toast ref={toast} />
      <h2 className="text-center">CALLEJERO</h2>

      {!mostrarNota ? (
        <>
          <div className="calle-actual">
            {getCalle()}

            {!clickHabilitado && ultimoResultado !== null && (
              <>
                <br />
                <span className="text-danger">
                  Has pinchado en {ultimoResultado}. Haz clic en el mapa para avanzar a la siguiente calle
                </span>
              </>
            )}

            {!clickHabilitado && ultimoResultado === null && (
              <>
                <br />
                <span className="text-success">¡Has acertado! Haz clic en el mapa para avanzar a la siguiente calle</span>
              </>
            )}
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <span>
              <Checkbox name="ayuda" value={ayuda} onChange={onAyudaChange} checked={ayuda} /> Mostrar ayuda en tiempo real
            </span>

            {zoomWarning && (
              <span style={{ color: "red", fontWeight: "600" }}>
                Por favor, haz más zoom para confirmar tu selección
              </span>
            )}
          </div>


          <br />

          <div style={{ fontSize: 12, opacity: 0.8 }}>
  Zoom actual: {currentZoom} (mínimo para confirmar selección: {MIN_ZOOM_TO_CONFIRM})
</div>

          <MapContainer center={position} zoom={zoom} scrollWheelZoom={false} style={{ height: "500px", width: "100%" }}>
            <TileLayer attribution={attributionLayer} url={urlLayer} />

            {ayuda && (
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution='Copyright: <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                opacity={0.9}
              />
            )}

            <ClickHandler onStreetClick={handleStreetClick} />
            <SetViewOnChange position={position} zoom={zoom} />
            <EnableZoomWithCtrl />
            <TrackZoom onZoomChange={setCurrentZoom} />
            <MapZoomClassController currentZoom={currentZoom} minZoom={MIN_ZOOM_TO_CONFIRM} />
          </MapContainer>
        </>
      ) : (
        <Dialog
          visible={true}
          style={{ width: "50vw" }}
          onHide={recargarPagina}
          draggable={false}
          resizable={false}
          modal={true}
          showHeader={true}
          closable={true}
          header="Callejero finalizado"
        >
          <div className="container">
            <h3 className="text-center text-primary">Tu porcentaje de aciertos ha sido del:</h3>
            <h1 className="text-center text-success">{porcentajeAciertos}%</h1>
          </div>
        </Dialog>
      )}
    </>
  );
};

export default MapaMudo;
